export const HISTORY_PERIODS = [
  { id: '7d', days: 7, label: '7 jours', short: '7 j' },
  { id: '30d', days: 30, label: '30 jours', short: '30 j' },
  { id: '90d', days: 90, label: '90 jours', short: '90 j' },
  { id: 'all', days: null, label: 'Tout l’historique', short: 'Tout' },
]

export function periodIndexFromId(periodId) {
  const index = HISTORY_PERIODS.findIndex((item) => item.id === periodId)
  return index >= 0 ? index : HISTORY_PERIODS.length - 1
}

export function periodIdFromIndex(index) {
  const numeric = Number(index)
  const clamped = Number.isFinite(numeric)
    ? Math.min(HISTORY_PERIODS.length - 1, Math.max(0, Math.round(numeric)))
    : HISTORY_PERIODS.length - 1
  return HISTORY_PERIODS[clamped].id
}

const DAY_MS = 24 * 60 * 60 * 1000

export function parsePrice(value) {
  if (value == null || value === '') return null
  const numeric = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'))
  if (!Number.isFinite(numeric) || numeric <= 0) return null
  return numeric
}

export function parseTime(value) {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

export function normalizeCurrency(value) {
  const code = String(value || 'EUR').trim().toUpperCase()
  return code || 'EUR'
}

export function historyBelongsToItem(payload, itemId) {
  if (!itemId) return false
  if (!payload || payload.item_id == null || payload.item_id === '') return true
  return String(payload.item_id) === String(itemId)
}

export function normalizeHistory(raw) {
  const list = Array.isArray(raw) ? raw : []
  const collected = []

  for (const entry of list) {
    const price = parsePrice(entry?.price)
    const time = parseTime(entry?.checked_at || entry?.created_at)
    if (price == null || time == null) continue
    collected.push({
      id: entry.id || `${time}-${price}`,
      price,
      time,
      checkedAt: new Date(time).toISOString(),
      currency: normalizeCurrency(entry?.currency),
    })
  }

  collected.sort((left, right) => left.time - right.time || String(left.id).localeCompare(String(right.id)))
  const byTime = new Map()
  for (const point of collected) {
    byTime.set(point.time, point)
  }
  const points = [...byTime.values()].sort((left, right) => left.time - right.time)
  const currencies = [...new Set(points.map((point) => point.currency))]

  return {
    points,
    currencies,
    mixed: currencies.length > 1,
  }
}

export function filterByPeriod(points, periodId, now = Date.now()) {
  const period = HISTORY_PERIODS.find((item) => item.id === periodId) || HISTORY_PERIODS[3]
  if (!period.days) return Array.isArray(points) ? points : []
  const from = now - period.days * DAY_MS
  return (points || []).filter((point) => point.time >= from)
}

export function summarizePoints(points) {
  if (!points?.length) return null
  const prices = points.map((point) => point.price)
  const last = points[points.length - 1]
  return {
    last: last.price,
    lastAt: last.checkedAt,
    min: Math.min(...prices),
    max: Math.max(...prices),
    count: points.length,
    currency: last.currency,
  }
}

export function classifyHistoryState({ loading, error, allPoints, periodPoints, mixed } = {}) {
  if (loading) return 'loading'
  if (error) return 'error'
  if (mixed) return 'mixed'
  if (!allPoints?.length) return 'empty'
  if (!periodPoints?.length) return 'period-empty'
  if (periodPoints.length === 1) return 'single'
  return 'multiple'
}

export function isHistoryAccessError(error) {
  const status = error?.response?.status ?? error?.status
  return status === 401 || status === 403 || status === 404
}

export function historyErrorMessage(error) {
  const status = error?.response?.status ?? error?.status
  if (status === 401 || status === 403) {
    return 'Accès à l’historique refusé. Reconnectez-vous puis réessayez.'
  }
  if (status === 404) {
    return 'Ce produit est introuvable ou n’est plus accessible.'
  }
  if (status === 502 || (status && status >= 500)) {
    return 'Impossible de charger l’historique. Vérifiez la connexion puis réessayez.'
  }
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim() && !/item non trouvé/i.test(detail)) {
    return detail
  }
  return 'Impossible de charger l’historique. Vérifiez la connexion puis réessayez.'
}

export function chartBounds(points, now = Date.now()) {
  if (!points?.length) {
    return { x: [now - DAY_MS, now], y: [0, 1] }
  }
  const times = points.map((point) => point.time)
  const prices = points.map((point) => point.price)
  let xMin = Math.min(...times)
  let xMax = Math.max(...times)
  if (xMin === xMax) {
    const pad = 12 * 60 * 60 * 1000
    xMin -= pad
    xMax += pad
  } else {
    const pad = (xMax - xMin) * 0.06
    xMin -= pad
    xMax += pad
  }
  let yMin = Math.min(...prices)
  let yMax = Math.max(...prices)
  if (yMin === yMax) {
    const pad = Math.max(0.5, yMin * 0.08)
    yMin = Math.max(0, yMin - pad)
    yMax += pad
  } else {
    const pad = (yMax - yMin) * 0.12
    yMin = Math.max(0, yMin - pad)
    yMax += pad
  }
  return { x: [xMin, xMax], y: [yMin, yMax] }
}

export function pickTimeTicks(points, max = 6) {
  if (!points?.length) return []
  if (points.length <= max) return points.map((point) => point.time)
  const last = points.length - 1
  const ticks = []
  for (let index = 0; index < max; index += 1) {
    const at = Math.round((index / (max - 1)) * last)
    ticks.push(points[at].time)
  }
  return [...new Set(ticks)]
}

export function formatAxisDate(time, spanMs = DAY_MS) {
  const date = new Date(time)
  if (!Number.isFinite(date.getTime())) return ''
  if (spanMs < 36 * 60 * 60 * 1000) {
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  if (spanMs < 400 * DAY_MS) {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
  }
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
}

export function formatLongDate(value) {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatAxisEuro(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ''
  const rounded = Math.abs(numeric) >= 10 ? Math.round(numeric) : numeric
  const digits = Number.isInteger(rounded) ? 0 : 2
  return `${rounded.toLocaleString('fr-FR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  })}\u00a0€`
}

export function toChartRows(points) {
  return (points || []).map((point) => ({
    time: point.time,
    price: point.price,
    checkedAt: point.checkedAt,
    currency: point.currency,
  }))
}

export function buildHistoryView({
  loading = false,
  error = null,
  payload = null,
  itemId = null,
  periodId = 'all',
  now = Date.now(),
} = {}) {
  if (payload && itemId && !historyBelongsToItem(payload, itemId)) {
    return {
      state: 'loading',
      points: [],
      allPoints: [],
      mixed: false,
      summary: null,
      stale: true,
    }
  }

  const normalized = normalizeHistory(payload?.history)
  const periodPoints = normalized.mixed ? [] : filterByPeriod(normalized.points, periodId, now)
  const state = classifyHistoryState({
    loading,
    error,
    allPoints: normalized.points,
    periodPoints,
    mixed: normalized.mixed,
  })

  return {
    state,
    points: periodPoints,
    allPoints: normalized.points,
    mixed: normalized.mixed,
    summary: summarizePoints(periodPoints),
    stale: false,
    bounds: chartBounds(periodPoints, now),
    ticks: pickTimeTicks(periodPoints),
    rows: toChartRows(periodPoints),
    spanMs: periodPoints.length > 1
      ? Math.max(DAY_MS, periodPoints[periodPoints.length - 1].time - periodPoints[0].time)
      : 2 * DAY_MS,
  }
}
